import React from 'react';
import { View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';

import { Button, Dialog } from '../../components';
import Text from '../../components/text/Text';
import { getUnitsLabel } from '../../utils/unitsLabel';
import { useThemedStyles } from '../../hooks/useThemedStyles';

import { SHORT_DATED_DAYS } from './PendingStockHelpers';
import PendingStockBatchRows from './PendingStockBatchRows';
import { createPendingStockDialogStyles } from './PendingStockDialog.styles';

const PendingStockResolveDialog = ({
	isOpen,
	onClose,
	productName,
	quantityInUnits,
	warehouseProduct,
	rows,
	updateRow,
	addRow,
	removeRow,
	normalizedQuantity,
	isShortDated,
	hasExpiredStock,
	isRequiredExpiryDateMissing,
	isExpiryDateRecommended,
	hasUnknownExpiryDate,
	canSubmit,
	onDiscard,
	onMoveToWarehouse,
	moveLoading,
	discardLoading,
	loading,
}) => {
	const styles = useThemedStyles(createPendingStockDialogStyles);

	return (
		<Dialog
			isOpen={isOpen}
			onClose={onClose}
		>
			<View style={styles.dialogContainer}>
				<KeyboardAwareScrollView
					style={styles.dialogScroll}
					contentContainerStyle={styles.dialogContent}
					enableOnAndroid={true}
					extraScrollHeight={32}
					keyboardShouldPersistTaps="handled"
					showsVerticalScrollIndicator={false}
				>
					<Text style={styles.dialogTitle}>Resolve pending stock</Text>
					<Text style={styles.dialogMeta}>{productName}</Text>
					<Text style={styles.dialogMeta}>Maximum available: {quantityInUnits} {getUnitsLabel(quantityInUnits)}</Text>
					<PendingStockBatchRows
						rows={rows}
						warehouseProduct={warehouseProduct}
						updateRow={updateRow}
						addRow={addRow}
						removeRow={removeRow}
						totalText={`Total selected: ${normalizedQuantity} / ${quantityInUnits} ${getUnitsLabel(quantityInUnits)}`}
						disabled={loading}
					/>
					{normalizedQuantity > quantityInUnits && (
						<Text style={styles.warningText}>
							Total selected is higher than the available pending stock.
						</Text>
					)}
					{isShortDated && (
						<Text style={styles.warningText}>
							Short dated: one or more expiry dates have less than {SHORT_DATED_DAYS} days remaining.
						</Text>
					)}
					{hasExpiredStock && (
						<Text style={styles.errorText}>
							Stock expiring today or earlier cannot be moved into saleable warehouse inventory.
							Discard it instead.
						</Text>
					)}
					{isRequiredExpiryDateMissing && (
						<Text style={styles.errorText}>
							An expiry date is required for every unit before this stock can be moved to the warehouse.
						</Text>
					)}
					{isExpiryDateRecommended && hasUnknownExpiryDate && (
						<Text style={styles.warningText}>
							Expiry dates are recommended for this product. Unknown dates can still be moved, but reduce expiry tracking accuracy.
						</Text>
					)}
				</KeyboardAwareScrollView>
				<View style={styles.dialogActions}>
					<Button
						style={styles.dialogActionButton}
						label="Discard stock"
						onPress={onDiscard}
						loading={discardLoading}
						disabled={!canSubmit || loading}
					/>
					<Button
						style={styles.dialogActionButton}
						label="Move to warehouse"
						onPress={onMoveToWarehouse}
						secondary={true}
						loading={moveLoading}
						disabled={!canSubmit || hasExpiredStock || isRequiredExpiryDateMissing || loading}
					/>
				</View>
			</View>
		</Dialog>
	);
};

export default PendingStockResolveDialog;
